import React, { useEffect, useRef, useState } from "react";
import {
  getWatermarkFontStack,
  WATERMARK_TEXT_FONTS,
} from "../../../watermark/Popup/watermarkSettings";
import {
  clearPendingTextLabelPick,
  duplicateSignPdfPlacement,
  getActiveResolvedSignPdfMark,
  getActiveSignPdfPlacement,
  applySignPdfBlockPositionPatch,
  patchActiveSignPdfPlacement,
  removeSignPdfPlacementOrBlock,
  resolveSignPdfCheckboxStyle,
  SIGN_PDF_CHECKBOX_STYLES,
  SIGN_PDF_DATE_FORMATS,
  SIGN_PDF_FIELD_KINDS,
  SIGN_PDF_FIELD_OPTIONS,
  SIGN_PDF_TEXT_LABEL_GROUPS,
  SIGN_PDF_DEFAULT_TEXT_LABEL,
  findSignPdfTextLabel,
  ungroupSignPdfBlock,
} from "./signPdfPlacements";
import {
  SIGN_PDF_DEFAULT_SIZE_PX,
  resolveSignPdfFontSizePx,
  signPdfFontSizeFieldsFromPx,
} from "./signPdfSettings";
import { CuteDropdown } from "./SignPdfCuteDropdown";
import { SignPdfPositionDropdown } from "./SignPdfPositionDropdown";
import {
  SignPdfCheckboxBoxedIcon,
  SignPdfCheckboxCheckIcon,
  SignPdfCheckboxEmptyIcon,
  SignPdfCheckboxWrongIcon,
  SignPdfDatePickerIcon,
  SignPdfDeleteIcon,
  SignPdfDuplicateIcon,
  SignPdfUngroupIcon,
} from "./SignPdfToolbarIcons";
import "./SignPdfSignatureToolbar.css";
import "./SignPdfFieldToolbar.css";

const CHECKBOX_STYLE_ICONS = {
  empty: SignPdfCheckboxEmptyIcon,
  box: SignPdfCheckboxBoxedIcon,
  check: SignPdfCheckboxCheckIcon,
  wrong: SignPdfCheckboxWrongIcon,
};

const FIELD_COLORS = [
  "#000000",
  "#111111",
  "#333333",
  "#666666",
  "#9E9E9E",
  "#E0E0E0",
  "#F5F5F5",
  "#FFFFFF",
  "#B71C1C",
  "#E53935",
  "#FF6F00",
  "#F9A825",
  "#2E7D32",
  "#00897B",
  "#1565C0",
  "#0D47A1",
];

const FIELD_SIZE_PRESETS = [7, 8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 30, 36, 48];

const FIELD_KINDS = new Set([
  SIGN_PDF_FIELD_KINDS.DATE,
  SIGN_PDF_FIELD_KINDS.TEXT,
  SIGN_PDF_FIELD_KINDS.CHECKBOX,
]);

function readOpacityPct(mark) {
  const n = Number(mark?.opacity);
  if (!Number.isFinite(n)) return 100;
  if (n > 0 && n <= 1) return Math.round(n * 100);
  return Math.max(0, Math.min(100, Math.round(n)));
}

const MONTH_LABELS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toIsoDate(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseIsoDate(value) {
  if (!value) return null;
  const m = String(value).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Compact black calendar panel — matches other toolbar dropdowns. */
function MiniCalendar({ valueIso, disabled = false, onPick }) {
  const selected = parseIsoDate(valueIso) || new Date();
  const [view, setView] = useState(() => ({
    year: selected.getFullYear(),
    month: selected.getMonth(),
  }));

  useEffect(() => {
    const next = parseIsoDate(valueIso);
    if (!next) return;
    setView({ year: next.getFullYear(), month: next.getMonth() });
  }, [valueIso]);

  const first = new Date(view.year, view.month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const todayIso = toIsoDate(new Date());
  const selectedIso = toIsoDate(selected);
  const cells = [];
  for (let i = 0; i < startPad; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);

  const shiftMonth = (delta) => {
    setView((prev) => {
      const d = new Date(prev.year, prev.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  return (
    <div className="sp-cal">
      <div className="sp-cal__head">
        <button
          type="button"
          className="sp-cal__nav"
          disabled={disabled}
          aria-label="Previous month"
          onClick={() => shiftMonth(-1)}
        >
          ‹
        </button>
        <span className="sp-cal__title">
          {MONTH_LABELS[view.month]} {view.year}
        </span>
        <button
          type="button"
          className="sp-cal__nav"
          disabled={disabled}
          aria-label="Next month"
          onClick={() => shiftMonth(1)}
        >
          ›
        </button>
      </div>
      <div className="sp-cal__weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="sp-cal__grid" role="grid">
        {cells.map((day, idx) => {
          if (day == null) {
            return <span key={`e-${idx}`} className="sp-cal__day is-empty" />;
          }
          const iso = `${view.year}-${pad2(view.month + 1)}-${pad2(day)}`;
          const isSelected = iso === selectedIso;
          const isToday = iso === todayIso;
          return (
            <button
              key={iso}
              type="button"
              role="gridcell"
              className={`sp-cal__day${isSelected ? " is-selected" : ""}${
                isToday ? " is-today" : ""
              }`}
              disabled={disabled}
              aria-selected={isSelected}
              onClick={() => onPick?.(iso)}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Sign-owned Date / Text / Checkbox toolbar — compact cute dropdowns.
 */
const SignPdfFieldToolbar = ({
  session,
  onSessionChange,
  currentPage = 1,
  visible = false,
  disabled = false,
  previewStageSize = { width: 420, height: 420 },
}) => {
  const [openId, setOpenId] = useState(null);
  const rootRef = useRef(null);

  const active = getActiveSignPdfPlacement(session);
  const isFieldKind = active && FIELD_KINDS.has(active.kind);

  useEffect(() => {
    if (!openId) return undefined;
    const onDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (e.target instanceof Element && e.target.closest(".sp-dd__menu")) {
        return;
      }
      setOpenId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openId]);

  useEffect(() => {
    if (!visible) {
      setOpenId(null);
      return;
    }
    const pendingId = session?.pendingTextLabelPickId || null;
    if (
      active?.kind === SIGN_PDF_FIELD_KINDS.TEXT &&
      pendingId &&
      active.id === pendingId &&
      !disabled
    ) {
      setOpenId("labels");
    } else {
      setOpenId(null);
    }
  }, [visible, disabled, active?.id, active?.kind, session?.pendingTextLabelPickId]);

  if (!visible || !active || !isFieldKind) return null;

  const mark = getActiveResolvedSignPdfMark(session, currentPage);
  if (!mark && active.kind !== SIGN_PDF_FIELD_KINDS.TEXT) return null;

  const kindLabel =
    SIGN_PDF_FIELD_OPTIONS.find((f) => f.id === active.kind)?.label || "Field";
  const isTextual =
    active.kind === SIGN_PDF_FIELD_KINDS.DATE ||
    active.kind === SIGN_PDF_FIELD_KINDS.TEXT;
  const color = String(
    mark?.logoTintColor ||
      mark?.color ||
      active.color ||
      (active.kind === SIGN_PDF_FIELD_KINDS.CHECKBOX ? "#9E9E9E" : "#111111")
  ).toUpperCase();
  const opacityPct = readOpacityPct(mark || { opacity: active.opacity ?? 100 });
  const opacitySliderBg = `linear-gradient(to right, #4f46e5 0%, #4f46e5 ${opacityPct}%, #2a2a2a ${opacityPct}%, #2a2a2a 100%)`;
  const bold = (mark?.fontWeight || active.fontWeight) === "bold";
  const italic = (mark?.fontStyle || active.fontStyle) === "italic";
  const fontId = mark?.fontId || active.fontId || "Arial";
  const fontMeta =
    WATERMARK_TEXT_FONTS.find((f) => f.id === fontId) || WATERMARK_TEXT_FONTS[0];
  const stageW = previewStageSize?.width || 420;
  const stageH = previewStageSize?.height || 420;
  const fontSizePx = Math.round(
    resolveSignPdfFontSizePx(active, stageW, stageH)
  );
  const textLabelDisplay = (() => {
    if (active.kind !== SIGN_PDF_FIELD_KINDS.TEXT) return "Label";
    const raw = active.text != null ? String(active.text) : "";
    if (!raw.trim()) return SIGN_PDF_DEFAULT_TEXT_LABEL;
    const preset = findSignPdfTextLabel(raw);
    return preset ? preset.label : raw;
  })();
  const dateFormatId = active.dateFormat || "MMM_D_YYYY";
  const dateFormatMeta =
    SIGN_PDF_DATE_FORMATS.find((f) => f.id === dateFormatId) ||
    SIGN_PDF_DATE_FORMATS.find((f) => f.id === "MMM_D_YYYY");
  const checkboxStyleId = resolveSignPdfCheckboxStyle(active);
  const CheckboxStyleIcon =
    CHECKBOX_STYLE_ICONS[checkboxStyleId] || SignPdfCheckboxBoxedIcon;

  const patch = (partial) => {
    if (disabled) return;
    onSessionChange?.((prev) => {
      const a = getActiveSignPdfPlacement(prev);
      const isBlockGeo =
        a?.blockId &&
        partial &&
        ("position" in partial ||
          "offsetX" in partial ||
          "offsetY" in partial ||
          "margin" in partial);
      if (isBlockGeo) {
        let next = applySignPdfBlockPositionPatch(
          prev,
          a.blockId,
          a.id,
          partial
        );
        const solo = { ...partial };
        delete solo.position;
        delete solo.offsetX;
        delete solo.offsetY;
        delete solo.margin;
        if (Object.keys(solo).length) {
          next = patchActiveSignPdfPlacement(next, solo, currentPage);
        }
        return next;
      }
      return patchActiveSignPdfPlacement(prev, partial, currentPage);
    });
  };

  const pickTextLabel = (text) => {
    if (disabled) return;
    onSessionChange?.((prev) => {
      let next = patchActiveSignPdfPlacement(prev, { text }, currentPage);
      next = clearPendingTextLabelPick(next);
      return next;
    });
    setOpenId(null);
  };

  const patchFontSize = (nextPx) => {
    const px = Math.max(7, Math.min(120, Math.round(Number(nextPx) || 16)));
    patch(
      signPdfFontSizeFieldsFromPx(px, stageW, stageH, {
        logo: active.kind === SIGN_PDF_FIELD_KINDS.CHECKBOX,
      })
    );
  };

  return (
    <div className="sp-sig-toolbar sp-field-toolbar" ref={rootRef} aria-live="polite">
      <div
        className={`sp-sig-toolbar__bar sp-field-toolbar__bar${
          disabled ? " is-disabled" : ""
        }`}
        role="toolbar"
        aria-label={`${kindLabel} tools`}
      >
        <div className="sp-sig-toolbar__scroll">
          <span className="sp-field-toolbar__kind">{kindLabel}</span>

          {active.kind === SIGN_PDF_FIELD_KINDS.DATE ? (
            <>
              <CuteDropdown
                id="format"
                openId={openId}
                setOpenId={setOpenId}
                label="Format"
                title={
                  dateFormatMeta
                    ? `Format: ${dateFormatMeta.label}`
                    : "Format"
                }
                disabled={disabled}
                menuClassName="sp-dd__menu--formats"
                display={
                  <span className="sp-dd__truncate">Format</span>
                }
              >
                {SIGN_PDF_DATE_FORMATS.map((fmt) => (
                  <button
                    key={fmt.id}
                    type="button"
                    role="option"
                    aria-selected={dateFormatId === fmt.id}
                    className={`sp-dd__option${
                      dateFormatId === fmt.id ? " is-selected" : ""
                    }`}
                    disabled={disabled}
                    onClick={() => {
                      patch({ dateFormat: fmt.id });
                      setOpenId(null);
                    }}
                  >
                    {fmt.label}
                  </button>
                ))}
              </CuteDropdown>
              <CuteDropdown
                id="datepicker"
                openId={openId}
                setOpenId={setOpenId}
                label="Pick date"
                title="Pick date"
                disabled={disabled}
                className="sp-dd--date-picker"
                triggerClassName="sp-dd__trigger--icon"
                hideChevron
                menuWidth={228}
                menuClassName="sp-dd__menu--calendar"
                display={
                  <SignPdfDatePickerIcon className="sp-sig__icon sp-sig__icon--date" />
                }
              >
                <MiniCalendar
                  valueIso={
                    active.dateValue
                      ? String(active.dateValue).slice(0, 10)
                      : toIsoDate(new Date())
                  }
                  disabled={disabled}
                  onPick={(iso) => {
                    patch({ dateValue: iso || null });
                    setOpenId(null);
                  }}
                />
              </CuteDropdown>
            </>
          ) : null}

          {active.kind === SIGN_PDF_FIELD_KINDS.DATE ? (
            <span className="sp-sig-divider" aria-hidden="true" />
          ) : null}

          {active.kind === SIGN_PDF_FIELD_KINDS.CHECKBOX ? (
            <>
              <CuteDropdown
                id="checkbox-style"
                openId={openId}
                setOpenId={setOpenId}
                label="Checkmark style"
                title="Checkmark style"
                disabled={disabled}
                className="sp-dd--checkbox-style"
                triggerClassName="sp-dd__trigger--icon"
                hideChevron
                menuWidth={56}
                menuClassName="sp-dd__menu--checkbox-styles"
                display={
                  <CheckboxStyleIcon
                    className="sp-sig__icon sp-sig__icon--checkbox"
                    style={{ color }}
                  />
                }
              >
                {SIGN_PDF_CHECKBOX_STYLES.map((style) => {
                  const Icon = CHECKBOX_STYLE_ICONS[style.id];
                  const selected = checkboxStyleId === style.id;
                  return (
                    <button
                      key={style.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`sp-dd__option sp-dd__option--icon-only${
                        selected ? " is-selected" : ""
                      }`}
                      title={style.label}
                      aria-label={style.label}
                      disabled={disabled}
                      onClick={() => {
                        patch({ checkboxStyle: style.id });
                        setOpenId(null);
                      }}
                    >
                      {Icon ? (
                        <Icon className="sp-sig__icon sp-sig__icon--checkbox-opt" />
                      ) : null}
                    </button>
                  );
                })}
              </CuteDropdown>
              <span className="sp-sig-divider" aria-hidden="true" />
            </>
          ) : null}

          {active.kind === SIGN_PDF_FIELD_KINDS.TEXT ? (
            <CuteDropdown
              id="labels"
              openId={openId}
              setOpenId={setOpenId}
              label="Label"
              title={`Label: ${textLabelDisplay}`}
              disabled={disabled}
              className="sp-dd--labels"
              menuClassName="sp-dd__menu--labels"
              menuWidth={180}
              display={textLabelDisplay}
            >
              <button
                type="button"
                role="option"
                aria-selected={
                  String(active.text || "") === SIGN_PDF_DEFAULT_TEXT_LABEL
                }
                className={`sp-dd__option${
                  String(active.text || "") === SIGN_PDF_DEFAULT_TEXT_LABEL
                    ? " is-selected"
                    : ""
                }`}
                disabled={disabled}
                onClick={() => pickTextLabel(SIGN_PDF_DEFAULT_TEXT_LABEL)}
              >
                {SIGN_PDF_DEFAULT_TEXT_LABEL}
              </button>
              {SIGN_PDF_TEXT_LABEL_GROUPS.map((group) => (
                <div key={group.id} className="sp-dd__group">
                  <div className="sp-dd__heading" role="presentation">
                    {group.heading}
                  </div>
                  {group.items.map((item) => {
                    const selected = String(active.text || "") === item.text;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`sp-dd__option${
                          selected ? " is-selected" : ""
                        }`}
                        disabled={disabled}
                        onClick={() => pickTextLabel(item.text)}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              ))}
            </CuteDropdown>
          ) : null}

          <CuteDropdown
            id="color"
            openId={openId}
            setOpenId={setOpenId}
            label="Color"
            disabled={disabled}
            menuClassName="sp-dd__menu--swatches"
            display={
              <span className="sp-dd__swatch-preview">
                <span
                  className="sp-dd__swatch-dot"
                  style={{ backgroundColor: color }}
                />
                Color
              </span>
            }
          >
            <div className="sp-dd__swatches">
              {FIELD_COLORS.map((c) => {
                const selected = color === String(c).toUpperCase();
                return (
                  <button
                    key={c}
                    type="button"
                    className={`sp-dd__swatch${selected ? " is-selected" : ""}`}
                    title={c}
                    aria-label={`Color ${c}`}
                    disabled={disabled}
                    onClick={() => {
                      patch({ color: c });
                      setOpenId(null);
                    }}
                  >
                    <span
                      className="sp-dd__swatch-chip"
                      style={{ backgroundColor: c }}
                      aria-hidden="true"
                    />
                    <span>{c}</span>
                  </button>
                );
              })}
            </div>
          </CuteDropdown>

          {isTextual ? (
            <>
              <CuteDropdown
                id="font"
                openId={openId}
                setOpenId={setOpenId}
                label="Font"
                disabled={disabled}
                menuClassName="sp-dd__menu--fonts"
                display={
                  <span
                    className="sp-dd__truncate"
                    style={{ fontFamily: getWatermarkFontStack(fontId) }}
                  >
                    {fontMeta?.label || fontId}
                  </span>
                }
              >
                {WATERMARK_TEXT_FONTS.map((font) => {
                  const selected = fontId === font.id;
                  return (
                    <button
                      key={font.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={`sp-dd__option${selected ? " is-selected" : ""}`}
                      disabled={disabled}
                      style={{ fontFamily: getWatermarkFontStack(font.id) }}
                      onClick={() => {
                        patch({ fontId: font.id });
                        setOpenId(null);
                      }}
                    >
                      {font.label || font.id}
                    </button>
                  );
                })}
              </CuteDropdown>

              <CuteDropdown
                id="size"
                openId={openId}
                setOpenId={setOpenId}
                label="Size"
                disabled={disabled}
                menuClassName="sp-dd__menu--compact"
                display={`${fontSizePx}px`}
              >
                {FIELD_SIZE_PRESETS.map((px) => (
                  <button
                    key={px}
                    type="button"
                    role="option"
                    aria-selected={fontSizePx === px}
                    className={`sp-dd__option${
                      fontSizePx === px ? " is-selected" : ""
                    }`}
                    disabled={disabled}
                    onClick={() => {
                      patchFontSize(px);
                      setOpenId(null);
                    }}
                  >
                    {px}px
                  </button>
                ))}
              </CuteDropdown>

              <button
                type="button"
                className={`sp-dd__chip${bold ? " is-on" : ""}`}
                disabled={disabled}
                title="Bold"
                aria-label="Bold"
                aria-pressed={bold}
                onClick={() =>
                  patch({ fontWeight: bold ? "normal" : "bold" })
                }
              >
                <span className="sp-dd__glyph sp-dd__glyph--bold" aria-hidden="true">
                  B
                </span>
              </button>
              <button
                type="button"
                className={`sp-dd__chip${italic ? " is-on" : ""}`}
                disabled={disabled}
                title="Italic"
                aria-label="Italic"
                aria-pressed={italic}
                onClick={() =>
                  patch({ fontStyle: italic ? "normal" : "italic" })
                }
              >
                <span className="sp-dd__glyph sp-dd__glyph--italic" aria-hidden="true">
                  I
                </span>
              </button>
            </>
          ) : null}

          <SignPdfPositionDropdown
            mark={mark || active}
            openId={openId}
            setOpenId={setOpenId}
            disabled={disabled}
            onPatch={patch}
          />

          <CuteDropdown
            id="opacity"
            openId={openId}
            setOpenId={setOpenId}
            label="Opacity"
            disabled={disabled}
            className="sp-dd--opacity"
            menuClassName="sp-dd__menu--opacity-slider"
            display={`Opacity ${opacityPct}%`}
          >
            <div
              className="sp-field-opacity"
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <div className="sp-field-opacity__label">
                <span>Opacity</span>
                <span>{opacityPct}%</span>
              </div>
              <input
                type="range"
                className="sp-sig-opacity-slider"
                min={0}
                max={100}
                step={1}
                value={opacityPct}
                disabled={disabled}
                style={{ "--sp-sig-opacity-slider-bg": opacitySliderBg }}
                aria-label="Opacity"
                onChange={(e) => {
                  const n = Math.max(
                    0,
                    Math.min(100, Number(e.target.value) || 0)
                  );
                  patch({ opacity: n });
                }}
              />
            </div>
          </CuteDropdown>

          <span className="sp-sig-divider" aria-hidden="true" />

          {active.blockId ? (
            <button
              type="button"
              className="sp-dd__icon"
              disabled={disabled}
              title="Ungroup signing block"
              aria-label="Ungroup signing block"
              onClick={() => {
                if (disabled) return;
                setOpenId(null);
                onSessionChange?.((prev) =>
                  ungroupSignPdfBlock(prev, active.blockId)
                );
              }}
            >
              <SignPdfUngroupIcon />
            </button>
          ) : null}

          <button
            type="button"
            className="sp-dd__icon"
            disabled={disabled}
            title="Duplicate"
            aria-label="Duplicate"
            onClick={() => {
              if (disabled) return;
              setOpenId(null);
              onSessionChange?.((prev) =>
                duplicateSignPdfPlacement(prev, active.id, currentPage)
              );
            }}
          >
            <SignPdfDuplicateIcon />
          </button>

          <button
            type="button"
            className="sp-dd__icon"
            disabled={disabled}
            title={active.blockId ? "Delete signing block" : "Delete"}
            aria-label={active.blockId ? "Delete signing block" : "Delete"}
            onClick={() => {
              if (disabled) return;
              setOpenId(null);
              onSessionChange?.((prev) =>
                removeSignPdfPlacementOrBlock(prev, active.id)
              );
            }}
          >
            <SignPdfDeleteIcon />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignPdfFieldToolbar;
